<template>
  <div>
    <b-modal id="shelf"
             header-bg-variant="primary"
             hide-footer
             body-bg-variant="dark"
             body-text-variant="white">
      <template class="bg-success" #modal-title>
        <span class="text-white">Choose shelf for this package <b-icon-bookshelf/></span>
      </template>
      <h3>{{ shelfBox.metaData || 'Select box to place' }}</h3>
      <b-form-select v-model="shelfBox.shelf"
                     :options="shelfOptions"
                     class="my-3 bg-dark text-light"
                     placeholder="Select shelf number"></b-form-select>
      <b-button class="mt-3 px-3 ml-1 bg-success float-right" @click="setLastLocation">
        SAVE
      </b-button>
      <b-button class="mt-3 px-3 bg-dark float-right" @click="$bvModal.hide('shelf')">
        Cancel
      </b-button>
    </b-modal>

    <b-modal id="packed-today"
             size="xl"
             scrollable
             hide-footer
             header-bg-variant="primary"
             body-bg-variant="dark"
             body-text-variant="white">
      <template #modal-title>
        <span class="text-white">
          <b-icon-box-seam class="mr-2"/>
          Packed today — {{ moment().format('DD.MM.YYYY') }}
          <b-badge variant="light" class="ml-2">{{ packedToday.length }}</b-badge>
        </span>
      </template>

      <b-overlay :show="packedTodayLoading" rounded="sm" variant="dark">
        <div v-if="!packedToday.length && !packedTodayLoading"
             class="text-center p-5 bg-dark rounded">
          Nothing has been packed today yet.
        </div>

        <template v-else>
          <div class="d-flex justify-content-end mb-2">
            <b-button size="sm" variant="light" @click="printPackedToday">
              <b-icon-printer class="mr-1"/> PRINT
            </b-button>
          </div>

          <b-table :items="packedToday"
                   :fields="packedTodayFields"
                   dark
                   small
                   striped
                   hover
                   responsive
                   sort-by="packedAt"
                   :sort-desc="true">
            <template #cell(packedAt)="{ item }">
              {{ moment(item.packedAt).format('HH:mm') }}
            </template>
            <template #cell(metaData)="{ item }">
              <strong>{{ item.metaData }}</strong>
            </template>
            <template #cell(count)="{ item }">
              <b-badge variant="info">{{ item.count }}</b-badge>
            </template>
            <template #cell(city)="{ item }">
              <span v-if="cityFromProducts(item.products)">
                <b-icon-geo-alt-fill class="mr-1"/>{{ cityFromProducts(item.products) }}
              </span>
            </template>
            <template #cell(products)="{ item }">
              <span v-for="(p, idx) in item.products" :key="idx" class="mr-2">
                <small>
                  {{ p.name }}<span class="text-muted"> ×{{ p.count }}</span><span v-if="idx < item.products.length - 1">,</span>
                </small>
              </span>
            </template>
            <template #cell(actions)="{ item }">
              <b-icon-upc-scan
                class="pointer"
                v-b-tooltip.hover.left="'Print QR sticker'"
                @click="printQR({ metaData: item.metaData, packedAt: item.packedAt })"/>
            </template>
          </b-table>
        </template>
      </b-overlay>
    </b-modal>
    <div class="page-head">
      <div>
        <h3>Packages</h3>
        <p class="page-head-sub">Find packed orders and track where they are</p>
      </div>
      <div class="page-head-actions">
        <b-button variant="success" @click="openPackedToday">
          <b-icon-box-seam class="mr-2"></b-icon-box-seam>Packed today
        </b-button>
      </div>
    </div>
    <div class="toolbar">
      <div class="toolbar-field">
        <b-form-input
          class="bg-dark text-white"
          @keyup.enter="getBoxes"
          v-model="search"
          placeholder="Search orders by metadata"></b-form-input>
      </div>
      <b-button @click="getBoxes" variant="primary" class="px-4" :disabled="search.length < 3"><b-icon-search class="mr-2"></b-icon-search>Search</b-button>
    </div>
    <b-overlay class="mt-3" :show="loading" rounded="sm" variant="dark">
    <div v-if="!boxes.length && !loading" class="empty-state">
      <b-icon-box></b-icon-box>
      Use the search box above to find packed orders by metadata (at least 3 characters).
    </div>
      <div class="orders-list">
        <div v-for="(row, i) in boxes" :key="i" class="order-card">
          <div :class="[getClassForMetadata(row), 'item-header d-flex align-items-center justify-content-between p-2 font-weight-bold rounded-top']">
            <div class="d-flex align-items-center">
              <b-icon-upc-scan @click="printQR(row.box)" class="pointer mr-2"></b-icon-upc-scan>
              <span v-if="getStatus(row.products[0]).label === 'NORWAY'" class="d-flex align-items-center mr-2">
          <b-icon-check
            v-if="isDelivered(row.products[0])"
            class="text-secondary mr-2"></b-icon-check>
          <b-icon-archive
            v-else
            @click="setAsDelivered(row.box)"
            class="pointer mr-2"></b-icon-archive>
        </span>
              <span
                v-b-tooltip.hover.top="'Ordered: ' + moment(row.products[0].addedAt).format('DD.MM.YYYY')"
              >
          {{ moment(row.box.packedAt).format('DD.MM.YYYY') }} - {{ row.box.metaData }}
        </span>
            </div>
            <span
              class="pointer text-right"
              @click="getStatus(row.products[0]).label=== 'NORWAY' && openBoxShelfDialog(row.box)"
              v-b-tooltip.hover.left="moment(getStatus(row.products[0]).timestamp).format('DD.MM.YYYY')"
            >
        {{ getStatus(row.products[0]).label }}
        <span v-if="getStatus(row.products[0]).label === 'NORWAY'">
          {{ row.box.lastLocation || '' }}
        </span>
      </span>
          </div>
          <table class="table table-hover table-dark mb-0">
            <tbody>
            <tr
              v-for="(product, y) in row.products"
              :key="y"
              :class="getRowClass(product)"
              v-b-tooltip.hover.right="product.notes ? stripHtmlTags(product.notes) : ''"
            >
              <td
                v-if="product.metaData === 'WORKSHOP'"
                @click="unloadBox(product._id)"
                class="p-2"
                style="width:40px"
              >
                <b-icon-upload class="pointer"></b-icon-upload>
              </td>
              <td class="p-2">{{ product.sku }}</td>
              <td class="p-2">{{ product.name }}</td>
              <td class="p-2">{{ product.ssku }}</td>
              <td class="p-2 text-center" style="width:70px">
                {{ product.count }}{{ product.replacedWithLocal && '-L' }}
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import fixed from '@/fixed';
import { metadataMapping, orderLead } from '@/utils';
import axios from 'axios';
import moment from 'moment';

import packageSticker from '@/sticker';
import transport from '@/views/Transport.vue';

export default {
  components: {},
  data: () => ({
    loading: false,
    boxes: [],
    summary: {},
    search: '',
    selectedCategory: '',
    selectedDate: '',

    shelfOptions: [
      { value: null, text: 'Select a shelf number' },
      ...Array.from({ length: fixed.localShelfCount }, (_, i) => ({ value: `${i + 1}`, text: `${i + 1}` })),
    ],
    shelfBox: {
      packedAt: '',
      metaData: '',
      shelf: null,
    },

    packedToday: [],
    packedTodayLoading: false,
    packedTodayFields: [
      {
        key: 'packedAt', label: 'Time', sortable: true, thStyle: { width: '80px' },
      },
      { key: 'metaData', label: 'Meta', sortable: true },
      {
        key: 'count', label: 'Items', sortable: true, thStyle: { width: '80px' }, class: 'text-center',
      },
      {
        key: 'city', label: 'City', sortable: true, thStyle: { width: '140px' },
      },
      { key: 'products', label: 'Products' },
      {
        key: 'actions', label: '', thStyle: { width: '60px' }, class: 'text-center',
      },
    ],

    moment,
  }),
  mounted() {},
  computed: {
    transport() {
      return transport;
    },
  },
  methods: {
    cityFromProducts(products) {
      if (!Array.isArray(products) || !products.length) return '';
      const withDelivery = products.find((p) => p && p.deliveryMethod && p.deliveryMethod.name);
      const deliveryName = withDelivery?.deliveryMethod?.name || '';
      if (!deliveryName) return '';
      if (deliveryName.startsWith('Postnord')) return 'POSTNORD';
      const match = deliveryName.match(/(?<=\d{4} )[^/]+(?= \/)/);
      return match ? match[0] : '';
    },
    async openPackedToday() {
      this.$bvModal.show('packed-today');
      this.packedTodayLoading = true;
      try {
        const { data } = await axios.get(`${fixed.API_URL}/packed-on-date`, {
          withCredentials: true,
          params: { date: moment().format('YYYY-MM-DD') },
        });
        this.packedToday = data || [];
      } catch (error) {
        this.$toast.error("can't fetch today's packed orders");
        this.packedToday = [];
      } finally {
        this.packedTodayLoading = false;
      }
    },
    printPackedToday() {
      if (!this.packedToday.length) {
        this.$toast.warning('Nothing to print');
        return;
      }

      const escapeHtml = (str) => String(str ?? '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[c]));

      const rows = [...this.packedToday]
        .sort((a, b) => new Date(a.packedAt) - new Date(b.packedAt))
        .map((row, idx) => {
          const time = moment(row.packedAt).format('HH:mm');
          const city = this.cityFromProducts(row.products);
          return `
            <tr>
              <td class="num">${idx + 1}</td>
              <td class="time">${time}</td>
              <td class="meta">${escapeHtml(row.metaData)}</td>
              <td class="city">${escapeHtml(city)}</td>
              <td class="count">${row.count}</td>
            </tr>`;
        })
        .join('');

      const totalPackages = this.packedToday.length;
      const totalItems = this.packedToday.reduce((sum, r) => sum + (r.count || 0), 0);
      const today = moment().format('DD.MM.YYYY');

      const html = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <title>Packed today — ${today}</title>
  <style>
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
      color: #111;
      padding: 16mm 12mm;
    }
    h1 {
      font-size: 18pt;
      margin: 0 0 4px 0;
    }
    .sub {
      font-size: 10pt;
      color: #555;
      margin-bottom: 14px;
    }
    .sub strong { color: #111; }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 10pt;
    }
    thead th {
      text-align: left;
      border-bottom: 2px solid #111;
      padding: 6px 8px;
      font-size: 9pt;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    tbody td {
      padding: 6px 8px;
      border-bottom: 1px solid #ddd;
      vertical-align: top;
    }
    tbody tr:nth-child(even) td { background: #fafafa; }
    .num { width: 28px; color: #888; text-align: right; }
    .time { width: 56px; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .meta { font-weight: 600; }
    .city { font-weight: 600; text-transform: uppercase; white-space: nowrap; }
    .count { width: 70px; text-align: center; font-weight: 600; }
    tfoot td {
      padding: 8px;
      border-top: 2px solid #111;
      font-weight: 600;
    }
    @media print {
      body { padding: 10mm; }
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <h1>Packed today</h1>
  <div class="sub">
    <strong>${today}</strong>
    &nbsp;·&nbsp; ${totalPackages} package${totalPackages === 1 ? '' : 's'}
    &nbsp;·&nbsp; ${totalItems} item${totalItems === 1 ? '' : 's'}
  </div>
  <table>
    <thead>
      <tr>
        <th class="num">#</th>
        <th>Time</th>
        <th>Meta</th>
        <th>City</th>
        <th class="count">Items</th>
      </tr>
    </thead>
    <tbody>${rows}
    </tbody>
    <tfoot>
      <tr>
        <td colspan="2">Total</td>
        <td>${totalPackages} package${totalPackages === 1 ? '' : 's'}</td>
        <td></td>
        <td class="count">${totalItems}</td>
      </tr>
    </tfoot>
  </table>
</body>
</html>`;

      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);

      iframe.onload = () => {
        this.$nextTick(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          // Give the browser a moment to spool the job before tearing the iframe down
          setTimeout(() => document.body.removeChild(iframe), 1000);
        });
      };

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
    },
    async getBoxes() {
      if (!this.search || this.search.length < 3) { return; }

      this.loading = true;
      const url = `${fixed.API_URL}/packed-orders`;
      try {
        const { data } = await axios.get(
          url,
          {
            withCredentials: true,
            params: { q: this.search },
          },
        );
        this.boxes = data || [];
      } catch (error) {
        this.$toast.error("can't fetch packages");
      }
      this.calculateSummary();
      this.loading = false;
    },
    async unloadBox(id) {
      if (!id) return;

      this.loading = true;
      try {
        await axios.put(`${fixed.API_URL}/poland/unload-box`, { id }, { withCredentials: true });
        await this.getBoxes();
        this.$toast.success('successfully unloaded box');
      } catch (error) {
        this.$toast.error('error while trying to unload box');
      }
      this.loading = false;
    },
    resetShelf() {
      this.shelfBox = {
        packedAt: '',
        metaData: '',
        shelf: '',
      };
    },
    openBoxShelfDialog(box) {
      this.shelfBox.metaData = box.metaData;
      this.shelfBox.packedAt = new Date(box.packedAt).toISOString();
      this.shelfBox.shelf = box.lastLocation || null;
      this.$bvModal.show('shelf');
    },
    async setLastLocation() {
      if (!this.shelfBox.packedAt || this.shelfBox.metaData === '' || !this.shelfBox.shelf) {
        this.$toast.error('Need shelf info to set');
        return;
      }

      this.loading = true;
      try {
        await axios.put(`${fixed.API_URL}/admin/set-package-shelf`, { ...this.shelfBox }, { withCredentials: true });
        this.resetShelf();
        this.$bvModal.hide('shelf');
        this.$toast.success('Shelf set successfully.');
        await this.getBoxes();
      } catch (err) {
        this.$toast.error(err?.response?.data ? err.response.data : 'error setting shelf for the box');
      }
      this.loading = false;
    },
    calculateSummary() {
      this.summary = {};
      if (!this.boxes.length) return;

      this.boxes.forEach((box) => {
        const cn = this.getClassForMetadata(box);
        box['category'] = cn; // eslint-disable-line
        this.summary[cn] = this.summary[cn] ? this.summary[cn] += 1 : 1;
      });
    },
    getClassForMetadata(order) {
      const lead = orderLead(order.products);
      if (lead.orderedFor) return 'bg-dark';
      const mapped = metadataMapping.find((m) => m.pattern.test(order.box.metaData));
      return mapped.getClass(lead?.deliveryMethod?.name);
    },
    getRowClass(row) {
      if (row.notes && !row.noteType) return 'bg-primary';
      if (row.notes && row.noteType === 1) return 'wrong-ean text-white';
      if (row.notes && row.noteType === 2) return 'unavailable-goods text-white';
      if (row.notes && row.noteType === 3) return 'goods-replaced-with text-dark';
      if (row.notes && row.noteType === 4) return 'bg-danger text-muted';

      return '';
    },
    stripHtmlTags(html) {
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent || div.innerText || '';
    },
    getStatus(order) {
      if (order?.transport && order.transport.poland && !order.transport.norway) {
        return {
          label: 'SENT',
          timestamp: order.transport.poland,
        };
      }
      if (order?.transport && order.transport.poland && order.transport.norway) {
        return {
          label: 'NORWAY',
          timestamp: order.transport.norway,
        };
      }

      return {
        label: 'READY',
        timestamp: order.packedAt,
      };
    },
    async printQR(box) {
      if (!box.metaData || !box.packedAt) {
        this.$toast.error('need package data to generate sticker');
        return;
      }
      this.loading = true;
      try {
        const { data } = await axios.put(
          `${fixed.API_URL}/qr-sticker`,
          { metaData: box.metaData, packedAt: box.packedAt },
          { withCredentials: true },
        );

        if (!data?.products || !data.products.length) {
          this.$toast.warning('There will be no products on the sticker');
          this.loading = false;
          return;
        }

        const stickerData = Object.freeze({
          ...data,
          products: Object.freeze((data.products || []).map((p) => Object.freeze({ ...p }))),
        });
        const iframe = packageSticker(stickerData);
        iframe.onload = () => {
          this.$nextTick(() => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            document.body.removeChild(iframe);
          });
        };
      } catch (error) {
        this.$toast.error('error while trying to generate QR sticker');
      }
      this.loading = false;
    },
    isDelivered(product) {
      return product && product.transport && product.transport.delivered;
    },
    async setAsDelivered(box) {
      // eslint-disable-next-line
      const confirmed = confirm('Are you sure you want to set this package as delivered?');
      if (!confirmed) return;

      if (!box.metaData || !box.packedAt) {
        this.$toast.error('need package data to set as delivered');
        return;
      }

      this.loading = true;
      try {
        await axios.put(
          `${fixed.API_URL}/admin/set-as-delivered`,
          { metaData: box.metaData, packedAt: box.packedAt },
          { withCredentials: true },
        );
        this.$toast.success('Package set as delivered');
        await this.getBoxes();
      } catch (err) {
        this.$toast.error(err?.response?.data ? err.response.data : 'error while trying to set package as delivered');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
<style>
.orders-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.order-card {
  overflow: hidden;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  break-inside: avoid;
}
.order-card .table {
  border: none;
  border-radius: 0;
  box-shadow: none;
}
.order-card .item-header {
  border-radius: 0 !important;
}
.item-header {
  min-height: 54px;
  padding: 0.6rem 1rem !important;
}

.regos-tape {
  background: #fada08;
  color: #222222;
}
.pickup-location {
  background: #f8f9fa;
  color: #222222;
}
.test-two-lines-tape {
  background-image: linear-gradient(to bottom, red 50%, blue 50%);
}
.test-three-lines-tape {
  background-image: linear-gradient(to bottom,
  #c050c9 0%, #c050c9 33.3%,
  #212126 33.3%, #212126 66.6%,
  #c050c9 66.6%, #c050c9 100%);
}
</style>
